import Image from "next/image";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import {
  openModal,
  ProductsByCategoriesQuery,
  resetCurrentlySubmittingQuickAddDeviationProducts,
  selectDeviationProducts,
  setCurrentlySubmittingQuickAddDeviationProduct,
  setCurrentlySubmittingQuickAddDeviationVariationId,
  updateAddonToDeviation,
} from "@chef/state-management";
import {
  getBasketDataFromCalendar,
  getDiscountedAmount,
  getImageFromImages,
  getPriceOfProductByVariation,
  getStockData,
} from "@chef/state-management/helpers";
import { Card, CurrencyWithDiscount, IconButton } from "@chef/components";
import { language } from "@chef/constants";
import { Check, Loading, Minus, Plus } from "@chef/icons/small";
import { useDebounce, useEffectOnce, usePrevious } from "@chef/hooks";
import { formatDate } from "@chef/helpers";
import {
  useAppSelector,
  useCalendarData,
  useTrack,
} from "@chef/state-management/hooks";

import { useSubmitDeviationChange } from "./useSubmitDeviationChange";

import { intl } from "./QuickAddStandaloneCard.Intl";

interface QuickAddStandaloneCardProps {
  variation: ProductsByCategoriesQuery["productsByCategories"][number]["products"][number]["variations"][number];
  week: number;
  year: number;
}

export const QuickAddStandaloneCard = ({
  variation,
  week,
  year,
}: QuickAddStandaloneCardProps) => {
  const dispatch = useDispatch();
  const track = useTrack();

  const { calendar } = useCalendarData({ week, year });

  const { submitDeviation, isSubmittingDeviation } = useSubmitDeviationChange({
    week,
    year,
  });

  const deviationProducts = useSelector(
    selectDeviationProducts({ week, year }),
  );

  const currentlySubmittingQuickAddDeviationProducts = useAppSelector(
    (state) => state.basket.currentlySubmittingQuickAddDeviationProducts,
  );

  const isUpdatingThisDeviation = useAppSelector(
    (state) =>
      state.basket.currentlySubmittingQuickAddDeviationVariationId ===
      variation.variationId,
  );

  const dirty = useRef(false);

  const [quantity, setQuantity] = useState<number>(0);

  const prevQuantity = usePrevious(quantity);

  const debouncedDeviationProducts = useDebounce(
    deviationProducts,
    1500,
    () => quantity > 0,
  );
  const prevDebouncedDeviationProducts = usePrevious(
    debouncedDeviationProducts,
  );

  useEffect(() => {
    if (prevQuantity === undefined || prevQuantity === quantity) {
      return;
    }

    const updatedProduct = {
      productId: variation.product.productId,
      productTypeId: variation.product.productTypeId,
      quantity: quantity,
      variationId: variation.variationId,
      price: price || 0,
    };

    dispatch(
      setCurrentlySubmittingQuickAddDeviationProduct({
        deviationProduct: updatedProduct,
      }),
    );

    dispatch(
      setCurrentlySubmittingQuickAddDeviationVariationId({
        variationId: variation.variationId,
      }),
    );

    dispatch(updateAddonToDeviation({ week, year, product: updatedProduct }));

    dirty.current = true;
  }, [quantity]);

  useEffect(() => {
    const isBasketUnchanged = debouncedDeviationProducts?.every(
      (pddp) =>
        prevDebouncedDeviationProducts?.some(
          (ddp) =>
            ddp.variationId === pddp.variationId &&
            ddp.quantity === pddp.quantity,
        ) &&
        debouncedDeviationProducts.length ===
          prevDebouncedDeviationProducts?.length,
    );

    if (isBasketUnchanged) {
      return;
    }

    const submitAndDispatch = async () => {
      if (dirty.current && deviationProducts && isUpdatingThisDeviation) {
        try {
          await submitDeviation(deviationProducts);
        } finally {
          dirty.current = false;
          dispatch(resetCurrentlySubmittingQuickAddDeviationProducts());
        }
      }
    };

    submitAndDispatch();
  }, [debouncedDeviationProducts]);

  const price = variation && getPriceOfProductByVariation(variation);

  const discountedAmount = variation && getDiscountedAmount(variation);

  useEffectOnce(() => {
    track("quickAddStandaloneProductShown", {
      product_name: variation.product.name,
      variation_name: variation?.name,
      week,
      year,
      price,
      discounted_amount: discountedAmount,
    });
  }, variation !== undefined && price !== undefined && discountedAmount !== undefined);

  if (!calendar) {
    return null;
  }

  const basketData = getBasketDataFromCalendar({
    calendar,
    week,
    year,
  });

  if (!basketData || !variation || !price || discountedAmount === undefined) {
    return null;
  }

  const stockData = getStockData({
    addedQuantity: quantity,
    variation,
  });

  const handleRemoveAllProducts = () => {
    setQuantity(0);
  };

  const handleShowProductModal = () => {
    dispatch(
      openModal({
        id: "STANDALONE_PRODUCT",
        productId: variation.product.productId,
        variationId: variation.variationId,
        week,
        year,
        view: "conversion",
      }),
    );
  };

  const mealboxBasket = basketData.mealboxBasket;

  const formattedDeliveryDate =
    mealboxBasket &&
    formatDate(
      new Date(
        mealboxBasket.shippingDetails.plannedDeliveryInformation.deliveryDate,
      ),
      "EEE d. MMM",
      { lowerCase: true },
    );

  const isUpdatingCurrentCard =
    currentlySubmittingQuickAddDeviationProducts.find(
      (p) => p.variationId === variation.variationId,
    ) !== undefined;

  const hasFooter = isUpdatingCurrentCard || quantity > 0;

  return (
    <Card
      className={clsx(
        "h-full grid items-stretch grid-cols-4 gap-x-2 text-start",
        isSubmittingDeviation &&
          "opacity-50 pointer-events-none hover:cursor-wait",
      )}
      noPadding
    >
      <div className="relative h-32 col-span-1 hover:cursor-pointer">
        <Image
          src={getImageFromImages(variation)}
          alt={variation.product.name}
          fill
          onClick={handleShowProductModal}
          className={clsx(
            "rounded-tl object-cover",
            !hasFooter && "rounded-bl",
          )}
        />
      </div>

      <div className="flex flex-col self-stretch justify-between col-span-3 p-2 grow">
        <button
          className="text-left line-clamp-2"
          onClick={handleShowProductModal}
        >
          {variation.name}
        </button>

        <div className="flex flex-row items-end justify-between">
          <CurrencyWithDiscount
            language={language}
            total={price}
            discount={discountedAmount}
            emphasized
          />

          <div className="flex items-center gap-2">
            {quantity !== 0 && (
              <>
                <IconButton
                  label={intl.REMOVE}
                  disabled={isSubmittingDeviation}
                  onClick={() => setQuantity((q) => q - 1)}
                  size="small"
                >
                  <Minus />
                </IconButton>
                <p>{quantity}</p>
              </>
            )}

            <IconButton
              label={intl.ADD}
              disabled={
                quantity >= stockData.maxPurchasableAmount ||
                isSubmittingDeviation
              }
              size="small"
              variant="primary"
              onClick={() => setQuantity((q) => q + 1)}
            >
              <Plus />
            </IconButton>
          </div>
        </div>
      </div>

      {(isUpdatingThisDeviation || quantity > 0) && (
        <div className="flex items-center self-stretch px-2 py-3 text-xs rounded-b col-span-full bg-informationBG">
          {isUpdatingCurrentCard ? (
            <>
              <Loading className="animate-spin text-information" />

              <p className="ml-2">{intl.UPDATING_YOUR_ORDER}…</p>
            </>
          ) : (
            <>
              <Check className="text-information" />

              <p className="ml-2">
                {intl.ADDED_TO_YOUR_ORDER} {formattedDeliveryDate}
              </p>

              <button
                className="ml-1 underline"
                onClick={handleRemoveAllProducts}
              >
                <strong>{intl.REMOVE}</strong>
              </button>
            </>
          )}
        </div>
      )}
    </Card>
  );
};
